import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { PricingRow, Site, SourcingRow, Yard, YardMaterialPrice } from '../types';
import { hoursSince } from '../utils/format';

export type MapMode = 'idle' | 'selling' | 'cheapest';

export interface OrderRequest {
  siteId: string | null;
  siteName?: string;
  siteAddress: string;
  yardId: string;
  deliveredPerUnit: number;
  quantity?: number;
}

// Metro center (NW Arkansas). Adjust per deployment.
const METRO_CENTER: [number, number] = [-94.16, 36.18];

interface Feat {
  lon: number;
  lat: number;
  label: string;
  color: string;
  radius: number;
  props: Record<string, any>;
}

// color ramp green -> amber -> red over [0,1]
function ramp(t: number): string {
  const c = Math.max(0, Math.min(1, t));
  if (c < 0.5) {
    const k = c / 0.5;
    return `rgb(${Math.round(21 + k * (217 - 21))},${Math.round(128 + k * (119 - 128))},${Math.round(61 + k * (6 - 61))})`;
  }
  const k = (c - 0.5) / 0.5;
  return `rgb(${Math.round(217 + k * (220 - 217))},${Math.round(119 + k * (38 - 119))},${Math.round(6 + k * (38 - 6))})`;
}

export default function HomeMap({
  mode,
  material,
  yards,
  sites,
  prices,
  pricingRows,
  sourcingRows,
  selectedSite,
  onRequestOrder,
  onSelectSite,
}: {
  mode: MapMode;
  material: string;
  yards: Yard[];
  sites: Site[];
  prices: YardMaterialPrice[];
  pricingRows: PricingRow[];
  sourcingRows: SourcingRow[];
  selectedSite: { lat: number | null; lon: number | null; name?: string; address?: string; site_id?: string } | null;
  onRequestOrder: (req: OrderRequest) => void;
  onSelectSite: (siteId: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const loaded = useRef(false);
  const onRequestOrderRef = useRef(onRequestOrder);
  onRequestOrderRef.current = onRequestOrder;
  const onSelectSiteRef = useRef(onSelectSite);
  onSelectSiteRef.current = onSelectSite;

  // init once
  useEffect(() => {
    if (map.current || !container.current) return;
    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    const m = new mapboxgl.Map({
      container: container.current,
      style: isDark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
      center: METRO_CENTER,
      zoom: 9,
    });
    map.current = m;
    m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    m.on('load', () => {
      m.addSource('points', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      m.addLayer({
        id: 'point-circles',
        type: 'circle',
        source: 'points',
        paint: {
          'circle-radius': ['get', 'radius'],
          'circle-color': ['get', 'color'],
          'circle-stroke-width': ['case', ['get', 'hollow'], 2, 1],
          'circle-stroke-color': ['case', ['get', 'hollow'], '#2563eb', '#ffffff'],
          'circle-opacity': ['case', ['get', 'hollow'], 0.15, 0.5],
        },
      });
      m.addLayer({
        id: 'point-labels',
        type: 'symbol',
        source: 'points',
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 11,
          'text-offset': [0, 1.4],
          'text-anchor': 'top',
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': isDark ? '#f5f5f4' : '#1c1917',
          'text-halo-color': isDark ? '#0f1110' : '#ffffff',
          'text-halo-width': 1.4,
        },
      });

      const openOrder = (e: any) => {
        const f = e.features?.[0];
        if (!f) return;
        const p = f.properties || {};
        if (p.yardId && p.deliveredPerUnit != null && p.siteAddress) {
          e.preventDefault?.();
          onRequestOrderRef.current({
            siteId: p.siteId ?? null,
            siteName: p.siteName || undefined,
            siteAddress: p.siteAddress,
            yardId: p.yardId,
            deliveredPerUnit: Number(p.deliveredPerUnit),
          });
        }
      };
      m.on('contextmenu', 'point-circles', openOrder);

      // Hover tooltip: show the point's name.
      const hoverPopup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 12,
        className: 'map-tip',
      });
      m.on('mouseenter', 'point-circles', (e: any) => {
        m.getCanvas().style.cursor = 'pointer';
        const f = e.features?.[0];
        const name = f?.properties?.name;
        if (!name) return;
        const coords = (f.geometry as any).coordinates.slice();
        hoverPopup.setLngLat(coords).setText(name).addTo(m);
      });
      m.on('mouseleave', 'point-circles', () => {
        m.getCanvas().style.cursor = '';
        hoverPopup.remove();
      });

      // Left-click a site point -> select it (same as the dropdown).
      m.on('click', 'point-circles', (e: any) => {
        const p = e.features?.[0]?.properties || {};
        if (p.siteId && (p.kind === 'site-idle' || p.kind === 'site')) {
          hoverPopup.remove();
          onSelectSiteRef.current(String(p.siteId));
        }
      });

      loaded.current = true;
      renderFeatures();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function buildFeatures(): Feat[] {
    if (mode === 'idle') {
      const priceFor = new Map(
        prices.filter((p) => p.material === material).map((p) => [p.yard_id, p])
      );
      // Sites first (drawn underneath) as small navy dots, no labels.
      const siteFeats: Feat[] = sites
        .filter((s) => s.lat != null && s.lon != null)
        .map((s) => ({
          lon: Number(s.lon),
          lat: Number(s.lat),
          label: '',
          color: '#14264d',
          radius: 4,
          props: { kind: 'site-idle', siteId: s.site_id, name: s.name ?? s.site_id },
        }));
      // Yards on top, brand accent blue, labeled with price.
      const yardFeats: Feat[] = yards
        .filter((y) => (y as any)[material] === true && y.lat != null && y.lon != null)
        .map((y) => {
          const pr = priceFor.get(y.yard_id);
          const fresh = hoursSince(pr?.updated_at) < 48;
          return {
            lon: Number(y.lon),
            lat: Number(y.lat),
            label: pr?.price != null ? `$${Number(pr.price).toFixed(0)}` : y.name,
            color: '#2ea3f2',
            radius: fresh ? 9 : 6,
            props: { kind: 'yard', id: y.yard_id, name: y.name },
          };
        });
      return [...siteFeats, ...yardFeats];
    }

    if (mode === 'selling') {
      const pts = pricingRows.filter(
        (r) => r.site_lat != null && r.site_lon != null && r.delivered_price != null
      );
      const vals = pts.map((r) => r.delivered_price!) as number[];
      const min = Math.min(...vals, Infinity);
      const max = Math.max(...vals, -Infinity);
      const span = max - min || 1;
      return pts.map((r) => ({
        lon: Number(r.site_lon),
        lat: Number(r.site_lat),
        label: `$${r.delivered_price!.toFixed(2)}`,
        color: ramp((r.delivered_price! - min) / span),
        radius: 8,
        props: {
          kind: 'site',
          siteId: r.site_id,
          siteName: r.site_name ?? '',
          siteAddress: r.site_address ?? '',
          yardId: r.yard_id,
          deliveredPerUnit: r.delivered_price,
          name: r.site_name || r.site_address || r.site_id,
        },
      }));
    }

    // cheapest
    const feats: Feat[] = [];
    if (selectedSite?.lat != null && selectedSite?.lon != null) {
      feats.push({
        lon: Number(selectedSite.lon),
        lat: Number(selectedSite.lat),
        label: selectedSite.name || 'Site',
        color: '#2563eb',
        radius: 10,
        props: { kind: 'selected-site', hollow: true, name: selectedSite.name || 'Site' },
      });
    }
    const ranked = sourcingRows.filter(
      (r) => r.yard_lat != null && r.yard_lon != null && r.delivered_price != null
    );
    const n = ranked.length;
    ranked.forEach((r, i) => {
      feats.push({
        lon: Number(r.yard_lon),
        lat: Number(r.yard_lat),
        label: `$${r.delivered_price!.toFixed(2)}`,
        color: r.is_cheapest ? '#15803d' : ramp(n > 1 ? 0.45 + (0.55 * i) / (n - 1) : 0.5),
        radius: r.is_cheapest ? 11 : 8,
        props: {
          kind: 'yard-ranked',
          yardId: r.yard_id,
          yardName: r.yard_name ?? '',
          deliveredPerUnit: r.delivered_price,
          siteId: selectedSite?.site_id ?? null,
          siteName: selectedSite?.name ?? '',
          siteAddress: selectedSite?.address ?? '',
          isCheapest: r.is_cheapest,
          name: r.yard_name ?? r.yard_id,
        },
      });
    });
    return feats;
  }

  function renderFeatures() {
    const m = map.current;
    if (!m || !loaded.current) return;
    const feats = buildFeatures();
    const fc = {
      type: 'FeatureCollection' as const,
      features: feats.map((f) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [f.lon, f.lat] },
        properties: { label: f.label, color: f.color, radius: f.radius, ...f.props },
      })),
    };
    (m.getSource('points') as mapboxgl.GeoJSONSource | undefined)?.setData(fc as any);

    if (feats.length > 0) {
      const b = new mapboxgl.LngLatBounds();
      feats.forEach((f) => b.extend([f.lon, f.lat]));
      m.fitBounds(b, { padding: 70, maxZoom: 12, duration: 500 });
    }
  }

  // re-render on data/mode change
  useEffect(() => {
    renderFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, material, yards, prices, pricingRows, sourcingRows, selectedSite]);

  const legend =
    mode === 'cheapest'
      ? [
          { c: '#15803d', t: 'Cheapest yard' },
          { c: '#dc2626', t: 'Higher delivered $' },
          { c: '#2563eb', t: 'Delivery site' },
        ]
      : mode === 'selling'
      ? [
          { c: '#15803d', t: 'Lower delivered $' },
          { c: '#dc2626', t: 'Higher delivered $' },
        ]
      : [
          { c: '#2ea3f2', t: 'Yard (label = price)' },
          { c: '#14264d', t: 'Delivery site' },
        ];

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div ref={container} style={{ position: 'absolute', inset: 0 }} />
      <div className="map-legend">
        {legend.map((l) => (
          <div className="legend-row" key={l.t}>
            <span className="legend-swatch" style={{ background: l.c }} />
            {l.t}
          </div>
        ))}
        <div className="muted" style={{ marginTop: 4, fontSize: 11 }}>
          Right-click a point to create an order
        </div>
      </div>
    </div>
  );
}
